var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { retryNon4xx } from "shared/api/query-retry";
import { tokensAdminApi } from "../../api/tokensAdminApi";
var KEYS = {
    all: ["admin", "tokens"],
    paged: function (filters, page, limit) {
        return ["admin", "tokens", "paged", filters !== null && filters !== void 0 ? filters : {}, page, limit];
    },
    one: function (id) { return ["admin", "tokens", id]; },
    stats: ["admin", "tokens", "stats"],
    onchain: function (id) { return ["admin", "tokens", id, "onchain"]; },
    orders: function (id) { return ["admin", "tokens", id, "orders"]; },
    trades: function (id) { return ["admin", "tokens", id, "trades"]; },
    holders: function (id) { return ["admin", "tokens", id, "holders"]; },
    activity: function (id) { return ["admin", "tokens", id, "activity"]; },
};
export var useAdminTokensPaged = function (filters, page, limit) {
    if (limit === void 0) { limit = 12; }
    return useQuery({
        queryKey: KEYS.paged(filters, page, limit),
        queryFn: function () { return tokensAdminApi.getPaged(filters, page, limit); },
        staleTime: 30000,
        retry: retryNon4xx,
    });
};
export var useAdminToken = function (id) {
    return useQuery({
        queryKey: KEYS.one(id),
        queryFn: function () { return tokensAdminApi.getById(id); },
        enabled: !!id,
        staleTime: 30000,
        retry: retryNon4xx,
    });
};
export var useTokenDashboardStats = function () {
    return useQuery({
        queryKey: KEYS.stats,
        queryFn: function () { return tokensAdminApi.getDashboardStats(); },
        staleTime: 30000,
        retry: retryNon4xx,
    });
};
export var useCreateToken = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (payload) { return tokensAdminApi.create(payload); },
        onSuccess: function () { return qc.invalidateQueries({ queryKey: KEYS.all }); },
    });
};
export var useUpdateToken = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var id = _a.id, payload = _a.payload;
            return tokensAdminApi.update(id, payload);
        },
        onSuccess: function (data) {
            qc.invalidateQueries({ queryKey: KEYS.all });
            qc.invalidateQueries({ queryKey: KEYS.one(data.id) });
        },
    });
};
export var useDeleteToken = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (id) { return tokensAdminApi.deleteOne(id); },
        onSuccess: function () { return qc.invalidateQueries({ queryKey: KEYS.all }); },
    });
};
export var useSetTokenStatus = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var id = _a.id, status = _a.status;
            return tokensAdminApi.setStatus(id, status);
        },
        onSuccess: function (data) {
            qc.invalidateQueries({ queryKey: KEYS.all });
            qc.invalidateQueries({ queryKey: KEYS.one(data.id) });
        },
    });
};
export var useSetTokenVerification = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var id = _a.id, verified = _a.verified;
            return tokensAdminApi.setVerification(id, verified);
        },
        onSuccess: function (data) {
            qc.invalidateQueries({ queryKey: KEYS.all });
            qc.invalidateQueries({ queryKey: KEYS.one(data.id) });
        },
    });
};
export var useSetTokenStartingPrice = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var id = _a.id, startingPrice = _a.startingPrice;
            return tokensAdminApi.setStartingPrice(id, startingPrice);
        },
        onSuccess: function (data) { return qc.invalidateQueries({ queryKey: KEYS.one(data.id) }); },
    });
};
export var useSetTokenSupply = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var id = _a.id, totalSupply = _a.totalSupply;
            return tokensAdminApi.setSupply(id, totalSupply);
        },
        onSuccess: function (data) { return qc.invalidateQueries({ queryKey: KEYS.one(data.id) }); },
    });
};
export var useSetTokenGeo = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var id = _a.id, geo = _a.geo;
            return tokensAdminApi.setGeo(id, geo);
        },
        onSuccess: function (data) { return qc.invalidateQueries({ queryKey: KEYS.one(data.id) }); },
    });
};
export var useSetTokenImage = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var id = _a.id, image = _a.image;
            return tokensAdminApi.setImage(id, image);
        },
        onSuccess: function (data) {
            qc.invalidateQueries({ queryKey: KEYS.all });
            qc.invalidateQueries({ queryKey: KEYS.one(data.id) });
        },
    });
};
export var useAllocateToken = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var id = _a.id, userId = _a.userId, quantity = _a.quantity;
            return tokensAdminApi.allocate(id, { userId: userId, quantity: quantity });
        },
        onSuccess: function (_, _a) {
            var id = _a.id;
            qc.invalidateQueries({ queryKey: KEYS.one(id) });
            qc.invalidateQueries({ queryKey: KEYS.holders(id) });
        },
    });
};
export var useTokenOnchainStatus = function (id) {
    return useQuery({
        queryKey: KEYS.onchain(id),
        queryFn: function () { return tokensAdminApi.getOnchainStatus(id); },
        enabled: !!id,
        retry: retryNon4xx,
        refetchInterval: function (query) { var _a; return ((_a = query.state.data) === null || _a === void 0 ? void 0 : _a.status) === "pending" ? 4000 : false; },
    });
};
export var useListTokenOnchain = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (id) { return tokensAdminApi.listOnchain(id); },
        onSuccess: function (_data, id) {
            qc.invalidateQueries({ queryKey: KEYS.onchain(id) });
            qc.invalidateQueries({ queryKey: KEYS.one(id) });
        },
    });
};
export var useTokenOrders = function (id, params) {
    return useQuery({
        queryKey: __spreadArray(__spreadArray([], KEYS.orders(id), true), [params], false),
        queryFn: function () {
            var _a;
            return tokensAdminApi.getOrders(id, {
                side: params.side,
                status: params.status,
                userId: params.userId,
                page: params.page,
                limit: (_a = params.limit) !== null && _a !== void 0 ? _a : 20,
            });
        },
        enabled: !!id,
        staleTime: 15000,
        retry: retryNon4xx,
    });
};
export var useCancelTokenOrder = function (tokenId) {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (orderId) { return tokensAdminApi.cancelOrder(orderId); },
        onSuccess: function () {
            qc.invalidateQueries({ queryKey: KEYS.orders(tokenId) });
            qc.invalidateQueries({ queryKey: KEYS.one(tokenId) });
        },
    });
};
export var useTokenTrades = function (id, page, limit) {
    if (limit === void 0) { limit = 20; }
    return useQuery({
        queryKey: __spreadArray(__spreadArray([], KEYS.trades(id), true), [page, limit], false),
        queryFn: function () { return tokensAdminApi.getTrades(id, { page: page, limit: limit }); },
        enabled: !!id,
        staleTime: 15000,
        retry: retryNon4xx,
    });
};
export var useTokenHolders = function (id, page, limit) {
    if (limit === void 0) { limit = 20; }
    return useQuery({
        queryKey: __spreadArray(__spreadArray([], KEYS.holders(id), true), [page, limit], false),
        queryFn: function () { return tokensAdminApi.getHolders(id, { page: page, limit: limit }); },
        enabled: !!id,
        staleTime: 15000,
        retry: retryNon4xx,
    });
};
export var useTokenActivity = function (id, page, limit) {
    if (limit === void 0) { limit = 20; }
    return useQuery({
        queryKey: __spreadArray(__spreadArray([], KEYS.activity(id), true), [page, limit], false),
        queryFn: function () { return tokensAdminApi.getActivity(id, { page: page, limit: limit }); },
        enabled: !!id,
        staleTime: 15000,
        retry: retryNon4xx,
    });
};
